import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import React, { useId, useMemo } from "react";

function formatAppLabel(app) {
    if (!app?.code) return "";
    if (app.name && app.name !== app.code) {
        return `${app.code} — ${app.name}`;
    }
    return app.code;
}

/**
 * Dropdown of registered {@code mo_apps} rows (code + display name).
 */
export default function AppCodeSelect({
    apps = [],
    value = "",
    onChange,
    label = "App",
    fullWidth = true,
    autoFocus = false,
    /** When true, empty value means "All" (for list filters). When false, empty prompts selection (forms). */
    allowAll = false,
    size = "medium",
    sx,
}) {
    const labelId = useId();
    const normalizedValue = value || "";
    const options = useMemo(() => {
        const list = Array.isArray(apps) ? apps : [];
        if (!normalizedValue) return list;
        if (list.some((app) => app.code === normalizedValue)) return list;
        return [{ id: `orphan-${normalizedValue}`, code: normalizedValue, name: normalizedValue }, ...list];
    }, [apps, normalizedValue]);

    const labelShrunk = allowAll || normalizedValue !== "";

    return (
        <FormControl fullWidth={fullWidth} size={size} sx={sx}>
            <InputLabel id={labelId} shrink={labelShrunk}>
                {label}
            </InputLabel>
            <Select
                labelId={labelId}
                label={label}
                value={normalizedValue}
                onChange={(e) => onChange(e.target.value)}
                autoFocus={autoFocus}
                displayEmpty={allowAll}
                notched={labelShrunk}
                renderValue={
                    allowAll
                        ? (selected) => {
                              if (!selected) return "All";
                              const app = options.find((a) => a.code === selected);
                              return app ? formatAppLabel(app) : selected;
                          }
                        : undefined
                }
            >
                {allowAll ? (
                    <MenuItem value="">
                        All
                    </MenuItem>
                ) : (
                    <MenuItem value="" disabled>
                        <em>Select app</em>
                    </MenuItem>
                )}
                {options.map((app) => (
                    <MenuItem key={app.id || app.code} value={app.code}>
                        {formatAppLabel(app)}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
}
